import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Settings as SettingsIcon, Briefcase, FolderOpen, MessageSquare, 
  Mail, LogOut, Menu, X, Users, Star
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const statCards = stats ? [
    { label: 'Services', value: stats.services, icon: <Briefcase size={24} /> },
    { label: 'Projects', value: stats.projects, icon: <FolderOpen size={24} /> },
    { label: 'Contacts', value: stats.contacts, badge: stats.new_contacts, icon: <Mail size={24} /> },
    { label: 'Chat Messages', value: stats.chat_messages, badge: stats.unread_chat, icon: <MessageSquare size={24} /> },
  ] : [];

  return (
    <div data-testid="admin-dashboard-overview">
      <h1 className="text-2xl font-bold text-white mb-8">Dashboard Overview</h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-[#0A1121] border border-white/5 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#FF5E1A]/10 rounded-xl flex items-center justify-center text-[#FF5E1A]">
                {stat.icon}
              </div>
              {stat.badge > 0 && (
                <span className="bg-[#FF5E1A] text-white text-xs font-bold px-2 py-1 rounded-full">
                  {stat.badge} new
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
            <div className="text-[#A1B0CC] text-sm">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#0A1121] border border-white/5 rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/services" className="bg-[#050B14] border border-white/5 rounded-xl p-4 hover:border-[#FF5E1A]/40 transition-all text-center">
            <Briefcase size={24} className="mx-auto text-[#FF5E1A] mb-2" />
            <span className="text-white text-sm">Manage Services</span>
          </Link>
          <Link to="/admin/projects" className="bg-[#050B14] border border-white/5 rounded-xl p-4 hover:border-[#FF5E1A]/40 transition-all text-center">
            <FolderOpen size={24} className="mx-auto text-[#FF5E1A] mb-2" />
            <span className="text-white text-sm">Manage Projects</span>
          </Link>
          <Link to="/admin/contacts" className="bg-[#050B14] border border-white/5 rounded-xl p-4 hover:border-[#FF5E1A]/40 transition-all text-center">
            <Mail size={24} className="mx-auto text-[#FF5E1A] mb-2" />
            <span className="text-white text-sm">View Contacts</span>
          </Link>
          <Link to="/admin/chat" className="bg-[#050B14] border border-white/5 rounded-xl p-4 hover:border-[#FF5E1A]/40 transition-all text-center">
            <MessageSquare size={24} className="mx-auto text-[#FF5E1A] mb-2" />
            <span className="text-white text-sm">Chat Messages</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Services Management
const ServicesManagement = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  return (
    <div data-testid="admin-services">
      <h1 className="text-2xl font-bold text-white mb-8">Manage Services</h1>
      <div className="bg-[#0A1121] border border-white/5 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left p-4 text-[#A1B0CC] font-medium">Title</th>
              <th className="text-left p-4 text-[#A1B0CC] font-medium">Category</th>
              <th className="text-left p-4 text-[#A1B0CC] font-medium">Features</th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <tr key={service.id} className="border-b border-white/5 hover:bg-[#050B14]">
                <td className="p-4 text-white">{service.title}</td>
                <td className="p-4">
                  <span className="tech-badge text-xs px-2 py-1 rounded capitalize">{service.category}</span>
                </td>
                <td className="p-4 text-[#A1B0CC] text-sm">{service.features?.length || 0} features</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Projects Management
const ProjectsManagement = () => {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  return (
    <div data-testid="admin-projects">
      <h1 className="text-2xl font-bold text-white mb-8">Manage Projects</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-[#0A1121] border border-white/5 rounded-2xl overflow-hidden">
            <img src={project.image_url} alt={project.title} className="w-full h-40 object-cover" />
            <div className="p-4">
              <h3 className="text-white font-semibold mb-1">{project.title}</h3>
              <span className="tech-badge text-xs px-2 py-1 rounded capitalize">{project.category}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Testimonials Management
const TestimonialsManagement = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <h1 className="text-2xl font-bold text-white mb-8">Manage Testimonials</h1>
      <div className="grid md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <div key={testimonial.id} className="bg-[#0A1121] border border-white/5 rounded-2xl p-6">
            <div className="flex gap-1 mb-3">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} size={14} className="fill-[#FF5E1A] text-[#FF5E1A]" />
              ))}
            </div>
            <p className="text-[#A1B0CC] text-sm mb-4">&ldquo;{testimonial.content}&rdquo;</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF5E1A]/20 rounded-full flex items-center justify-center">
                <span className="text-[#FF5E1A] font-bold text-sm">{testimonial.name.charAt(0)}</span>
              </div>
              <div>
                <div className="text-white font-medium text-sm">{testimonial.name}</div>
                <div className="text-[#5B6B8A] text-xs">{testimonial.role}, {testimonial.company}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Contacts Management
const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contacts`, { withCredentials: true });
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  return (
    <div data-testid="admin-contacts">
      <h1 className="text-2xl font-bold text-white mb-8">Contact Inquiries</h1>
      <div className="space-y-4">
        {contacts.map((contact) => (
          <div key={contact.id} className="bg-[#0A1121] border border-white/5 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold">{contact.name}</h3>
                <p className="text-[#A1B0CC] text-sm">{contact.email} &bull; {contact.phone}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                contact.status === 'new' ? 'bg-[#FF5E1A]/20 text-[#FF5E1A]' : 'bg-green-500/20 text-green-400'
              }`}>
                {contact.status}
              </span>
            </div>
            <p className="text-[#A1B0CC] text-sm">{contact.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Chat Management
const ChatManagement = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  return (
    <div data-testid="admin-chat">
      <h1 className="text-2xl font-bold text-white mb-8">Chat Messages</h1>
      <div className="space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className="bg-[#0A1121] border border-white/5 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold">{msg.name}</h3>
                <p className="text-[#A1B0CC] text-sm">{msg.email}</p>
              </div>
              {!msg.is_read && (
                <span className="bg-[#FF5E1A] text-white text-xs font-bold px-2 py-1 rounded-full">New</span>
              )}
            </div>
            <p className="text-[#A1B0CC] text-sm">{msg.message}</p>
          </div>
        ))}
        {messages.length === 0 && (
          <p className="text-[#A1B0CC] text-center py-8">No chat messages yet.</p>
        )}
      </div>
    </div>
  );
};

// Main Dashboard Layout
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: <LayoutDashboard size={20} />, label: 'Dashboard' },
    { path: '/admin/services', icon: <Briefcase size={20} />, label: 'Services' },
    { path: '/admin/projects', icon: <FolderOpen size={20} />, label: 'Projects' },
    { path: '/admin/testimonials', icon: <Star size={20} />, label: 'Testimonials' },
    { path: '/admin/contacts', icon: <Mail size={20} />, label: 'Contacts' },
    { path: '/admin/chat', icon: <MessageSquare size={20} />, label: 'Chat' },
  ];

  return (
    <div className="min-h-screen bg-[#050B14] flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar border-r border-white/5 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF5E1A] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">M</span>
            </div>
            <div>
              <span className="text-white font-bold">Mohtech</span>
              <span className="text-[#A1B0CC] text-xs block">Admin Panel</span>
            </div>
          </Link>
        </div>

        <nav className="px-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === item.path
                  ? 'bg-[#FF5E1A]/10 text-[#FF5E1A]'
                  : 'text-[#A1B0CC] hover:bg-white/5 hover:text-white'
              }`}
            >
              {item.icon}
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-[#FF5E1A]/20 rounded-full flex items-center justify-center">
              <Users size={18} className="text-[#FF5E1A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-medium text-sm truncate">{user?.name}</div>
              <div className="text-[#5B6B8A] text-xs truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1B0CC] hover:bg-white/5 hover:text-white transition-all"
          >
            <LogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-40 bg-[#050B14] border-b border-white/5 px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white p-2"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <span className="text-white font-bold">Admin Panel</span>
            <div className="w-10" />
          </div>
        </header>

        {/* Content */}
        <main className="p-6 lg:p-8">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="services" element={<ServicesManagement />} />
            <Route path="projects" element={<ProjectsManagement />} />
            <Route path="testimonials" element={<TestimonialsManagement />} />
            <Route path="contacts" element={<ContactsManagement />} />
            <Route path="chat" element={<ChatManagement />} />
            <Route path="*" element={<DashboardOverview />} />
          </Routes>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
